import { addDays, format, isAfter, parseISO, subDays } from 'date-fns'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { HEX_COLOR_PATTERN } from './constants'
import { GamePromotionalMessage } from '@Generated/back-office-client/src'

/** Blank is a legal colour: it clears the field, which the endpoint reads as null. */
export const isValidHexColor = (value: string): boolean =>
  value.trim() === '' || HEX_COLOR_PATTERN.test(value.trim())

/**
 * Anything short of a complete #RRGGBB renders as the fallback. A swatch and a preview both need
 * a colour at every keystroke, including the half-typed ones `isValidHexColor` lets through.
 */
export const colorOrFallback = (value: string, fallback: string): string =>
  value.trim() && isValidHexColor(value) ? value.trim() : fallback

export const blankToNull = (value: string): string | null => value.trim() || null

/**
 * A date-only input carries no timezone, so it is read as midnight in the customer's. That has to be
 * the same zone the list formats in, or a saved window shifts a day every time it is reopened.
 */
export const toCustomerMidnight = (date: string | null, timeZone: string): Date | null =>
  date ? fromZonedTime(date, timeZone) : null

/**
 * The Ends field names the last day a message shows, so the instant stored is the start of the day
 * after it — `active_to` is exclusive server-side. Without the extra day, "ends Oct 8" would stop the
 * message overnight on the 7th and a one-day window could not be expressed at all.
 */
export const toCustomerEndOfDay = (date: string | null, timeZone: string): Date | null =>
  date ? fromZonedTime(addDays(parseISO(date), 1), timeZone) : null

/**
 * The inverse of {@link toCustomerEndOfDay}: the stored instant back to the last day shown.
 * The day is taken off the customer-zone calendar date rather than the instant, because a
 * fixed 24 hours is the wrong step across a daylight-saving boundary.
 */
export const toEndDateInput = (date: Date | null | undefined, timeZone: string): string | null => {
  if (!date) {
    return null
  }

  const dayAfter = formatInTimeZone(new Date(date), timeZone, 'yyyy-MM-dd')

  return format(subDays(parseISO(dayAfter), 1), 'yyyy-MM-dd')
}

/**
 * A window with no start is already live, and one with no end never stops. activeTo is exclusive,
 * matching the server's `active_to > NOW()`.
 */
export const isLive = (message: GamePromotionalMessage): boolean => {
  const now = new Date()

  if (message.activeFrom && isAfter(message.activeFrom, now)) {
    return false
  }

  return !message.activeTo || isAfter(message.activeTo, now)
}

/** Shared so a branding save on a game page refreshes the list it came from. */
export const editableGamesQueryKey = (customerId: string | null | undefined): string[] => [
  `${customerId}-editable-games`,
]
